import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Button, Card, Col, Empty, Input, Pagination, Row, Select, Spin, Tag } from 'antd'
import MainLayout from 'Hoc/MainLayout'
import Loader from 'Components/Loader'
import { fetchDepartments, fetchStores } from 'Actions/data-actions'
import api from 'Common/api'
import { formatDate, getDepartmentLabel, getPromoLocaliStatoMeta, handleServerError, PROMO_LOCALI_STATI } from 'Common/utils'
import { ROLE_CS } from 'Common/constants/global'
import {
  StyledH1,
  StyledRow,
  StyledForm,
  StyledFormItem,
  StyledButton,
  ActionRow,
} from './styled'

const { Option } = Select

const PAGE_SIZE = 10

const SearchPromoLocali = () => {
  const history = useHistory()
  const dispatch = useDispatch()

  const departments = useSelector((state) => state.Data.departments)
  const stores = useSelector((state) => state.Data.stores)
  const user = useSelector((state) => state.User)

  const [isLoading, setIsLoading] = useState(true)
  const [isSearching, setIsSearching] = useState(false)
  const [items, setItems] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState({
    department: undefined,
    stato: undefined,
    idStore: user && user.role === ROLE_CS ? user.store : undefined,
    description: undefined,
  })

  const fetchPage = useCallback((page, activeFilters) => {
    setIsSearching(true)

    const params = {
      page: page - 1,
      size: PAGE_SIZE,
    }

    if (activeFilters.department !== undefined && activeFilters.department !== null) {
      params.department = activeFilters.department
    }
    if (activeFilters.stato) {
      params.stato = activeFilters.stato
    }
    if (activeFilters.idStore !== undefined && activeFilters.idStore !== null) {
      params.idStore = activeFilters.idStore
    }
    if (activeFilters.description) {
      params.description = activeFilters.description
    }

    return api.promoLocali
      .list(params)
      .then((response) => {
        const data = response.data || {}
        setItems(data.content || [])
        setTotalElements(data.totalElements || 0)
      })
      .catch(handleServerError)
      .finally(() => setIsSearching(false))
  }, [])

  useEffect(() => {
    const promises = [dispatch(fetchDepartments())]

    if (stores.length === 0) {
      promises.push(dispatch(fetchStores()))
    }

    Promise.all(promises)
      .then(() => fetchPage(1, filters))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const handleSearch = () => {
    setCurrentPage(1)
    fetchPage(1, filters)
  }

  const handleReset = () => {
    const resetFilters = {
      department: undefined,
      stato: undefined,
      idStore: user && user.role === ROLE_CS ? user.store : undefined,
      description: undefined,
    }
    setFilters(resetFilters)
    setCurrentPage(1)
    fetchPage(1, resetFilters)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    fetchPage(page, filters)
  }

  const getStoreLabel = (idStore) => {
    if (idStore === null || idStore === undefined) {
      return '-'
    }
    const store = stores.find((s) => s.id === idStore)
    return store ? `${store.id}-${store.name}` : idStore
  }

  const renderField = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
      <span style={{ color: 'rgba(0, 0, 0, 0.45)', marginRight: 12 }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right' }}>{value}</span>
    </div>
  )

  const renderCard = (record) => {
    const meta = getPromoLocaliStatoMeta(record.stato)

    return (
      <Col xs={24} sm={12} lg={8} key={record.id}>
        <Card
          size="small"
          title={record.description || '-'}
          extra={<Tag color={meta.color === 'default' ? undefined : meta.color}>{meta.label}</Tag>}
          style={{ height: '100%', borderRadius: 12, overflow: 'hidden' }}
          actions={[
            <Button
              type="link"
              icon="eye"
              onClick={() => history.push(`/edit-promo-locali/${record.id}`)}
            >
              Apri
            </Button>,
          ]}
        >
          {renderField('ID', record.id)}
          {renderField('Anno', record.year || '-')}
          {renderField(
            'Reparto',
            record.department !== null && record.department !== undefined
              ? getDepartmentLabel(record.department, departments)
              : '-',
          )}
          {renderField('Negozio', getStoreLabel(record.idStore))}
          {renderField(
            'Periodo promo',
            `${formatDate(record.installationFrom, 'YYYY-MM-DD', 'DD/MM/YYYY') || '-'} ~ ${formatDate(record.installationTo, 'YYYY-MM-DD', 'DD/MM/YYYY') || '-'}`,
          )}
          {renderField('Data arrivo merce', formatDate(record.merchArrivalDate, 'YYYY-MM-DD', 'DD/MM/YYYY') || '-')}
        </Card>
      </Col>
    )
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <MainLayout
      tourSteps={[]}
      isTourAvailable={false}
      isTourOpen={false}
      defaultCurrentPage="dossier"
    >
      <StyledH1>Ricerca Promo Locali</StyledH1>
      <StyledRow type="flex" justify="center">
        <Col xs={22} xl={20} xxl={16}>
          <StyledForm layout="vertical">
            <StyledRow gutter={16} type="flex">
              <Col xs={24} sm={12} md={6}>
                <StyledFormItem label="Reparto" colon={false}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    value={filters.department}
                    onChange={(value) => setFilters((prev) => ({ ...prev, department: value }))}
                  >
                    {departments.map((department) => (
                      <Option key={department.id} value={department.id}>
                        {`${department.id}-${department.label}`}
                      </Option>
                    ))}
                  </Select>
                </StyledFormItem>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <StyledFormItem label="Stato" colon={false}>
                  <Select
                    allowClear
                    value={filters.stato}
                    onChange={(value) => setFilters((prev) => ({ ...prev, stato: value }))}
                  >
                    {PROMO_LOCALI_STATI.map((stato) => (
                      <Option key={stato.key} value={stato.key}>
                        {stato.label}
                      </Option>
                    ))}
                  </Select>
                </StyledFormItem>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <StyledFormItem label="Negozio" colon={false}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={user.role === ROLE_CS}
                    value={filters.idStore}
                    onChange={(value) => setFilters((prev) => ({ ...prev, idStore: value }))}
                  >
                    {(stores || []).map((store) => (
                      <Option key={store.id} value={store.id}>
                        {`${store.id}-${store.name}`}
                      </Option>
                    ))}
                  </Select>
                </StyledFormItem>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <StyledFormItem label="Descrizione" colon={false}>
                  <Input
                    value={filters.description}
                    onChange={(e) => setFilters((prev) => ({ ...prev, description: e.target.value }))}
                    onPressEnter={handleSearch}
                    allowClear
                  />
                </StyledFormItem>
              </Col>
              <Col xs={12} sm={6} md={4}>
                <StyledButton onClick={handleReset}>Reset</StyledButton>
              </Col>
              <Col xs={12} sm={6} md={4}>
                <StyledButton type="primary" loading={isSearching} onClick={handleSearch}>
                  Cerca
                </StyledButton>
              </Col>
            </StyledRow>
          </StyledForm>
        </Col>
      </StyledRow>
      <StyledRow type="flex" justify="center">
        <Col xs={22} xl={20} xxl={16}>
          <ActionRow>
            <Button type="primary" icon="plus" onClick={() => history.push('/promo-locali/create')}>
              Crea Promo Locali
            </Button>
          </ActionRow>
          <Spin spinning={isSearching}>
            {items.length === 0 && !isSearching
              ? <Empty description="Nessun risultato" style={{ marginTop: 48 }} />
              : (
                <Row gutter={[16, 16]} type="flex">
                  {items.map(renderCard)}
                </Row>
              )}
            {totalElements > PAGE_SIZE && (
              <Row type="flex" justify="end" style={{ marginTop: 16 }}>
                <Pagination
                  current={currentPage}
                  pageSize={PAGE_SIZE}
                  total={totalElements}
                  onChange={handlePageChange}
                />
              </Row>
            )}
          </Spin>
        </Col>
      </StyledRow>
    </MainLayout>
  )
}

export default SearchPromoLocali
